/**
 * Shared layout for the 4 shorter lessons. Each lesson is a small
 * component that returns { title, hook, interactive, body, nextSlug, nextTitle }.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BookStrip from "@/components/BookStrip";
import useSEO from "@/lib/useSEO";
import QuickAnswer from "@/components/QuickAnswer";

export default function LessonLayout({ meta, children, next }) {
  useSEO({
    title: `${meta.title} — Learn · Centennial`,
    description: meta.seoDescription,
    canonicalPath: `/learn/${meta.slug}`,
  });

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link
          to="/learn"
          className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339] mb-8"
        >
          <ArrowLeft size={14} /> Back to lessons
        </Link>

        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2">
          Lesson {meta.number} · {meta.time}
        </div>
        <h1 className="text-4xl sm:text-5xl font-medium text-[#1C2B23] leading-tight mb-4">
          {meta.title}
        </h1>
        <p className="text-lg text-[#5C6B62] leading-relaxed max-w-2xl">
          {meta.hook}
        </p>

        {meta.quickAnswer && (
          <QuickAnswer reviewed={meta.reviewed || "June 2026"}>{meta.quickAnswer}</QuickAnswer>
        )}

        <div className="mt-10 space-y-8">{children}</div>

        <BookStrip category={meta.bookCategory || "wealth"} title="If you liked this lesson" limit={3} />

        <div className="mt-14 flex items-center justify-between border-t border-[#E5E2DC] pt-6">
          <Link to="/learn" className="text-sm text-[#5C6B62] hover:text-[#2B5339]">
            ← All lessons
          </Link>
          {next && (
            <Link
              to={`/learn/${next.slug}`}
              className="bg-[#2B5339] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#1E3B28] inline-flex items-center gap-2"
            >
              Next: {next.title} →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
